import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Img } from "./Img";
import { HERO_TINY } from "../lib/heroPlaceholder";
import { ArrowDown } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.3 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section id="home" ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y, scale, backgroundImage: `url(${HERO_TINY})`, backgroundSize: "cover", backgroundPosition: "center" }} className="absolute inset-0 opacity-45">
        <Img name="hero" alt="Sculpt Fusion studio" priority sizes="100vw" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 h-full mx-auto max-w-[1400px] px-5 sm:px-8 flex flex-col justify-end pb-16 sm:pb-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 text-xs sm:text-sm uppercase tracking-[0.35em] text-neutral-400"
        >
          Mat Pilates Studio — El Centro, CA
        </motion.p>

        <h1 className="font-display uppercase leading-[0.82] tracking-tight text-[19vw] sm:text-[16vw] lg:text-[13vw]">
          <span className="block overflow-hidden">
            <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
              Sculpt
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-stroke-neon">
              Fusion
            </motion.span>
          </span>
        </h1>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-end gap-6 sm:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-md text-base lg:text-lg text-neutral-400 font-light"
          >
            45-minute mat classes built to burn, lengthen and rebuild. Sixteen mats. One glowing room. Zero excuses.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="flex gap-3"
          >
            <a
              href="#schedule"
              data-testid="hero-book-btn"
              className="inline-flex items-center bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200"
            >
              Reserve a Mat
            </a>
            <a
              href="#pricing"
              data-testid="hero-pricing-btn"
              className="inline-flex items-center border border-neutral-700 text-white text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-neon hover:text-neon transition-colors duration-200"
            >
              View Pricing
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 right-5 sm:right-8 z-10 hidden sm:flex items-center gap-2 text-neutral-400 text-xs uppercase tracking-[0.25em]"
      >
        Scroll <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
